import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ForVolunteersPage() {
  const benefits = [
    {
      icon: 'ri-calendar-check-line',
      title: '無理なく続けられるスケジュール',
      description: '自分のペースで参加できる柔軟なスケジュール設計'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '謝金の扱いが明確で安心',
      description: '透明性のある謝金システムで、安心して活動に集中'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '困ったらすぐ相談',
      description: 'サービス内報告やメールで、いつでもサポートを受けられる'
    },
    {
      icon: 'ri-heart-line',
      title: '継続的な成長支援',
      description: '長期的な視点で、あなたの成長をサポート'
    }
  ];

  const volunteerTypes = [
    {
      title: '大学生ボランティア',
      description: '学業と両立しながら、子どもたちの学習をサポート',
      features: ['柔軟なスケジュール', '学習経験を活かせる', '謝金で学費の足しに'],
      image: 'https://public.readdy.ai/ai/img_res/f88683070164162d149e9099b77c11c8.jpg'
    },
    {
      title: '若手社会人ボランティア',
      description: '仕事の経験を活かして、子どもたちの将来をサポート',
      features: ['平日夜・週末対応', '実務経験を共有', '社会貢献の実感'],
      image: 'https://readdy.ai/api/search-image?query=young%20Japanese%20professional%20volunteer%20helping%20Japanese%20children%20with%20homework%20in%20modern%20learning%20space%20in%20Japan%2C%20Japanese%20mentor%20and%20student%20interaction%2C%20professional%20yet%20friendly%20environment%2C%20educational%20support%20setting%2C%20warm%20atmosphere&width=400&height=300&seq=vol-2&orientation=landscape'
    }
  ];

  const steps = [
    {
      step: '1',
      title: '簡単登録',
      description: '基本情報と希望条件を入力'
    },
    {
      step: '2',
      title: 'マッチング',
      description: 'あなたに合った居場所を紹介'
    },
    {
      step: '3',
      title: '活動開始',
      description: '安心のサポート体制で活動スタート'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                「続けたい」を支える仕組み。
              </h1>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                ボラタスは、ボランティアの皆さまが無理なく長く続けられる環境を提供します。
                あなたの「良いことをしたい」気持ちを、しっかりとサポートします。
              </p>
              <p className="text-base text-teal-700 mb-8 leading-relaxed font-medium">
                ボランティアが無理なく続けられることが、こどもたちの安心と学びの継続性につながると考えています。
              </p>
              <a 
                href="https://boratasu.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                サービスを使ってみる
              </a>
            </div>
            <div className="lg:text-right">
              <img 
                src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20young%20Japanese%20volunteers%20helping%20Japanese%20children%20with%20studies%20in%20Japan%2C%20collaborative%20learning%20environment%2C%20smiling%20faces%2C%20educational%20materials%20and%20books%2C%20bright%20and%20welcoming%20Japanese%20classroom%20setting%2C%20warm%20atmosphere&width=600&height=400&seq=vol-3&orientation=landscape"
                alt="子どもたちと一緒に学ぶボランティアの様子"
                className="rounded-2xl shadow-lg object-cover w-full h-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
            ボランティアの皆さまのメリット
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Types Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
            こんな方にぴったり
          </h2>
          <div className="grid lg:grid-cols-2 gap-8">
            {volunteerTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
                <img 
                  src={type.image}
                  alt={type.title}
                  className="rounded-xl object-cover w-full h-48 mb-6"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-4">{type.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{type.description}</p>
                <div className="space-y-2">
                  {type.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-3">
                      <i className="ri-check-line text-teal-500"></i>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm leading-relaxed">
              ボランティアが無理なく続けられることが、こどもたちの安心と学びにつながると考えています。
            </p>
          </div>
        </div>
      </section>

      {/* How to Start Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4">
            始め方は簡単
          </h2>
          <p className="text-center text-gray-600 mb-12">
            あなたの「良いことをしたい」気持ちを、続けられる形に
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-2xl font-bold text-white">{step.step}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gray-300 -translate-y-0.5"></div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
            ボランティアの声
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">大学生</h4>
                  <p className="text-sm text-gray-600">文系学部・3年次</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                「学業と両立しながら続けられています。子どもたちの成長を見るのが何より嬉しいです。」
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">社会人2年目</h4>
                  <p className="text-sm text-gray-600">IT企業勤務</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                「仕事の経験を活かして子どもたちに教えられるのが楽しいです。サポート体制もしっかりしていて安心。」
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-500 to-teal-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            あなたも一緒に、子どもたちの未来を支えませんか？
          </h2>
          <p className="text-lg mb-8 opacity-90">
            登録は無料です。まずは気軽に始めてみてください。
          </p>
          <div className="text-center">
            <a 
              href="https://boratasu.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors text-lg font-semibold whitespace-nowrap cursor-pointer inline-block"
            >
              サービスを使ってみる
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
